<template>
    <div class="trademark-input-container">
        <label v-if="label.length != 0" class="trademark-input-label">
            {{ label }}
            <span v-if="required" class="required-indicator">*</span>
            <span v-if="$slots['label:happen']" class="label-happen"><slot name="label:happen"></slot></span>
        </label>
        <div class="trademark-input-wrapper">
            <label class="trademark-input-field" :class="{ 'has-error': error, 'is-disabled': disabled, 'is-success': success }">
                <div v-if="icon" class="input-icon">
                    <StfIcon :icon="icon" size="small" />
                </div>
                <input
                    type="text"
                    class="trademark-input"
                    :value="modelValue"
                    :placeholder="placeholder"
                    @input="
                        $emit(
                            'update:modelValue',
                            ($event.target as HTMLInputElement).value,
                        )
                    "
                    @keyup.enter="$emit('enter')"
                    :required="required"
                    :disabled="disabled"
                    :class="{ 'with-icon': icon }"
                    @focus="$emit('focus')"
                    ref="inputRef"
                />
            </label>
        </div>
        <div v-if="error && !noErrorMessage" class="trademark-input-error">{{ error }}</div>
    </div>
</template>

<script setup lang="ts">
import StfIcon from '@/ui/icon/StfIcon.vue';
import type {IconName} from '@/ui/icon/icons';
import { ref } from 'vue';

interface Props {
    modelValue: string;
    placeholder: string;
    label?: string;
    icon?: IconName;
    required?: boolean;
    error?: string | null;
    success?: boolean;
    disabled?: boolean;
    noErrorMessage?: boolean;
}
withDefaults(defineProps<Props>(), {
    modelValue: '',
    placeholder: '',
    label: '',
    required: false,
    success: false,
    error: null,
    disabled: false,
    noErrorMessage: false,
});

const inputRef = ref<HTMLInputElement | null>(null);

defineExpose({
    inputRef
});

defineEmits(['update:modelValue', 'enter', 'click', 'focus']);
</script>
<style lang="scss" scoped>
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

input[disabled] {pointer-events:none}

.trademark-input-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.trademark-input-label {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-14;
  font-weight: vars.$font-weight-semibold;
  line-height: 1.5;
  width: 100%;
}

.label-happen {
  float: right;
  padding-right: 5px;
}

.required-indicator {
  color: #ff3b30;
  margin-left: 2px;
}

.trademark-input-wrapper {
  display: flex;
  height: 40px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;
}

.trademark-input-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background-color: vars.$color-white;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) inset;
  border: 1px solid vars.$color-gray-300;
  position: relative;
  padding: 0 4px;
  box-sizing: border-box;

  &.has-error {
    border-color: vars.$color-error;
  }

  &.is-success {
    border-color: vars.$color-success;
  }
}

.input-icon {
  position: absolute;
  left: 10px;
  display: flex;
  align-items: center;
  z-index: 1;
}

.trademark-input {
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-13;
  line-height: 1.5;
  color: vars.$color-black;

  &.with-icon {
    padding-left: 32px;
  }

  &::placeholder {
    color: vars.$color-gray-300;
    font-weight: vars.$font-weight-semibold;
  }

  &:focus + .trademark-placeholder,
  &:not(:placeholder-shown) + .trademark-placeholder {
    opacity: 0;
  }

  @include mixins.mobile {
      font-size: vars.$font-size-16;
  }
}

.trademark-input-error {
  color: vars.$color-error;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  line-height: 1.4;
  margin-top: 2px;
}
</style>
